import React from 'react';
import { ShieldCheck, Zap, CheckCircle2, ArrowRight, Lock, Boxes, Stethoscope, Syringe, FileArchive, Building2 } from 'lucide-react';
import { UserPlanTier } from '../../components/PlanUpgradeModal';

interface EnterpriseGuardProps {
  currentTier: UserPlanTier;
  onUpgrade: () => void;
  onBack?: () => void;
  featureName?: string;
  featureDescription?: string;
  children: React.ReactNode;
}

export function EnterpriseGuard({
  currentTier,
  onUpgrade,
  onBack,
  featureName = 'Recurso do Plano Empresa',
  featureDescription = 'Disponível exclusivamente no Plano Empresa do R-MAX.',
  children
}: EnterpriseGuardProps) {
  const hasEnterpriseAccess = currentTier === 'enterprise';

  if (hasEnterpriseAccess) {
    return <>{children}</>;
  }

  const enterpriseFeatures = [
    {
      icon: Boxes,
      title: 'Controle Rigoroso de Estoque & Lotes',
      desc: 'Validade, rastreabilidade por lote, balancete financeiro e extrato estilo conta corrente.'
    },
    {
      icon: Stethoscope,
      title: 'Prontuário Eletrônico SOAP & Imutabilidade',
      desc: 'Anamnese, sinais vitais, hipótese e conduta protegidos por trigger no banco com validade jurídica.'
    },
    {
      icon: Syringe,
      title: 'Central de Retorno de Vacinas no WhatsApp',
      desc: 'Disparos via WhatsApp com pré-filtro da agenda online sem custos extras de API.'
    },
    {
      icon: FileArchive,
      title: 'Cofre de Exames & Imagens DICOM R2',
      desc: 'Guarda legal de 5 anos (CFMV), compressão WebP e visualizador com janela óssea.'
    },
    {
      icon: Building2,
      title: 'Portal do Contador Dedicado (Forma B)',
      desc: 'Acesso auditado para contabilidade com compliance LGPD e exportação para Domínio/Alterdata.'
    }
  ];

  return (
    <div className="max-w-4xl mx-auto py-10 px-4 font-sans">
      <div className="bg-white rounded-3xl border border-zinc-200 shadow-xl overflow-hidden">
        {/* Header Premium */}
        <div className="bg-linear-to-br from-neutral-950 via-zinc-900 to-neutral-900 text-white p-8 sm:p-10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
          
          <div className="relative z-10 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-black uppercase tracking-wider">
              <Zap className="h-3.5 w-3.5 text-blue-400" />
              <span>Plano Empresa • R$ 99,00 / mês</span>
            </div>

            <h1 className="text-2xl sm:text-4xl font-black tracking-tight text-white">
              {featureName}
            </h1>

            <p className="text-sm sm:text-base text-zinc-300 max-w-2xl font-medium leading-relaxed">
              {featureDescription} Desbloqueie todo o poder da gestão clínica, controle de insumos e auditoria contábil com o plano mais avançado do R-MAX.
            </p>
          </div>
        </div>

        {/* Corpo com a lista de benefícios em 100% texto estruturado */}
        <div className="p-8 sm:p-10 space-y-8 bg-zinc-50/50">
          <div className="space-y-4">
            <h2 className="text-xs font-black uppercase tracking-widest text-zinc-500">
              O que você recebe no Plano Empresa:
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {enterpriseFeatures.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="p-4 bg-white rounded-2xl border border-zinc-200/80 shadow-xs flex items-start gap-3.5">
                    <div className="p-2.5 bg-blue-50 text-blue-700 rounded-xl shrink-0 mt-0.5">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <strong className="text-xs font-black text-zinc-900 block">
                        {f.title}
                      </strong>
                      <p className="text-[11px] text-zinc-600 font-medium leading-relaxed">
                        {f.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Banner de Garantia e Preço */}
          <div className="p-5 bg-blue-50 border border-blue-200 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="space-y-0.5 text-center sm:text-left">
              <div className="text-xs font-black text-blue-950 uppercase tracking-wide">
                Assinatura Mensal Sem Fidelidade
              </div>
              <p className="text-[11px] text-blue-800 font-medium">
                Cancele a qualquer momento direto na plataforma. Suporte prioritário via WhatsApp.
              </p>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="w-full sm:w-auto py-3 px-4 bg-white hover:bg-zinc-100 text-zinc-700 font-bold text-xs rounded-xl border border-zinc-200 transition-all cursor-pointer"
                >
                  Voltar
                </button>
              )}
              <button
                type="button"
                onClick={onUpgrade}
                className="w-full sm:w-auto py-3 px-6 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-md flex items-center justify-center gap-2 transition-all cursor-pointer"
              >
                <span>Fazer Upgrade para Plano Empresa</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EnterpriseGuard;
